import type { Dispatch, SetStateAction } from "react";
import { getServerUrl } from "@app/dashboard/utils/serverUrls";
import { urlValidation } from "@app/dashboard/utils/formValidations";
import { COUNTRIES_INFO, COUNTRIES } from "@app/dashboard/constants/countries";
import type {
  BusinessInformationApiData,
  FormErrors,
  FormState,
  OriginalValues,
} from "@app/dashboard/types/innerLayout/business_profile";
import { DEFAULT_WHATSAPP_COUNTRY, MAX_BUSINESS_DESCRIPTION_LENGTH } from "@app/dashboard/constants/business-profile";

export const mapBusinessInfoApiDataToFormState = (data: BusinessInformationApiData): FormState => {
  const parsedWhatsapp = parsePhoneNumber(data.whatsapp_number ?? "");

  return {
    businessName: data.brand_name ?? "",
    businessDescription: data.business_description ?? "",
    logoImage: null,
    accountType: data.business ?? "",
    sector: data.sector?.id != null ? data.sector.id : "",
    industry: data.industry?.id != null ? data.industry.id : "",
    website: data.website_url ?? "",
    facebook: data.facebook_url ?? "",
    instagram: data.instagram_url ?? "",
    whatsappPhone: parsedWhatsapp?.fullNumber ?? "",
    whatsappCountry: parsedWhatsapp?.country ?? DEFAULT_WHATSAPP_COUNTRY,
  };
};

export const mapBusinessInfoApiDataToOriginalValues = (data: BusinessInformationApiData): OriginalValues => ({
  ...mapBusinessInfoApiDataToFormState(data),
  originalLogoUrl: handleImgPath(data.logo_image ?? null),
});

export const mapOriginalValuesToFormState = (original: OriginalValues): FormState => {
  const { originalLogoUrl: _originalLogoUrl, ...formState } = original;
  return {
    ...formState,
    logoImage: original.logoImage ?? null,
  };
};

export const validateBusinessProfileForm = (
  formState: FormState,
  setFormErrors: Dispatch<SetStateAction<FormErrors>>,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  validateUrlField("website", formState.website ?? "", setFormErrors, t);
  validateUrlField("facebook", formState.facebook ?? "", setFormErrors, t);
  validateUrlField("instagram", formState.instagram ?? "", setFormErrors, t);
  validateDescription(
    formState.businessDescription ?? "",
    MAX_BUSINESS_DESCRIPTION_LENGTH,
    setFormErrors,
    t
  );
  validateWhatsappPhone(
    formState.whatsappPhone ?? "",
    formState.whatsappCountry ?? DEFAULT_WHATSAPP_COUNTRY,
    setFormErrors,
    t
  );
};

export const buildBusinessProfileFormData = (
  formState: FormState,
  original: OriginalValues,
  logoWasDeleted: boolean
): FormData => {
  const formData = new FormData();

  const fieldMappings: Array<{
    formKey: keyof FormState;
    apiKey: string;
    transform?: (value: unknown) => string;
    hasChanged?: (current: FormState, baseline: OriginalValues) => boolean;
  }> = [
    { formKey: "businessName", apiKey: "brand_name" },
    { formKey: "businessDescription", apiKey: "business_description" },
    { formKey: "accountType", apiKey: "business" },
    {
      formKey: "sector",
      apiKey: "sector",
      transform: (val) => (val != null && val !== "" ? String(val) : ""),
    },
    {
      formKey: "industry",
      apiKey: "industry",
      transform: (val) => (val != null && val !== "" ? String(val) : ""),
    },
    { formKey: "website", apiKey: "website_url" },
    { formKey: "facebook", apiKey: "facebook_url" },
    { formKey: "instagram", apiKey: "instagram_url" },
    {
      formKey: "whatsappPhone",
      apiKey: "whatsapp_number",
      hasChanged: (current, baseline) =>
        current.whatsappPhone !== baseline.whatsappPhone ||
        current.whatsappCountry !== baseline.whatsappCountry,
    },
  ];

  fieldMappings.forEach(({ formKey, apiKey, transform, hasChanged }) => {
    const formValue = formState[formKey];
    const originalValue = original[formKey];
    const isChanged = hasChanged
      ? hasChanged(formState, original)
      : formValue !== originalValue;

    if (isChanged) {
      formData.append(apiKey, transform ? transform(formValue) : String(formValue ?? ""));
    }
  });

  if (formState.logoImage instanceof File) {
    formData.append("logo_image", formState.logoImage);
  } else if (logoWasDeleted && original.originalLogoUrl !== null) {
    formData.append("logo_image", "");
  }

  return formData;
};

export const handleImgPath = (path: string | null): string | null => {
    if (!path) return null;

    const baseUrl = getServerUrl();
    const isRelativePath = path.startsWith('/');
    return isRelativePath ? `${baseUrl}${path}` : path;
};

export const parsePhoneNumber = (phoneNumber: string | null | undefined): { country: string; number: string; fullNumber: string } => {
    if (!phoneNumber || typeof phoneNumber !== 'string' || !phoneNumber.startsWith('+')) {
        return { country: 'eg', number: phoneNumber || '', fullNumber: phoneNumber || '' };
    }

    if (phoneNumber.startsWith('+20')) {
        const number = phoneNumber.substring(2);
        return {
            country: 'eg',
            number: number,
            fullNumber: phoneNumber,
        };
    }

    const countryCodeMap: { code: string; iso2: string }[] = Object.values(COUNTRIES).map(country => ({
        code: COUNTRIES_INFO[country].phone_country_code,
        iso2: COUNTRIES_INFO[country].country_code_iso2.toLowerCase(),
    })).sort((a, b) => b.code.length - a.code.length);

    for (const { code, iso2 } of countryCodeMap) {
        if (phoneNumber.startsWith(code)) {
            const number = phoneNumber.substring(code.length);
            return {
                country: iso2,
                number: number,
                fullNumber: phoneNumber,
            };
        }
    }

    return { country: 'eg', number: phoneNumber.substring(1), fullNumber: phoneNumber };
};

export const extractPhoneNumberWithoutCode = (phoneNumber: string | null | undefined, countryIso2?: string): string => {
    const str = phoneNumber ?? "";
    if (!str || !str.startsWith('+')) {
        return str;
    }

    if (str.startsWith('+20')) {
        return str.substring(2);
    }

    if (countryIso2) {
        const countryInfo = Object.values(COUNTRIES).find(country =>
            COUNTRIES_INFO[country].country_code_iso2.toLowerCase() === countryIso2.toLowerCase()
        );

        if (countryInfo) {
            const phoneCountryCode = COUNTRIES_INFO[countryInfo].phone_country_code;
            if (str.startsWith(phoneCountryCode)) {
                return str.replace(phoneCountryCode, '');
            }
        }
    }

    const countryCodeMap: { code: string }[] = Object.values(COUNTRIES).map(country => ({
        code: COUNTRIES_INFO[country].phone_country_code,
    })).sort((a, b) => b.code.length - a.code.length);

    for (const { code } of countryCodeMap) {
        if (str.startsWith(code)) {
            return str.substring(code.length);
        }
    }

    return str.substring(1);
};

export const validateUrlField = (
    fieldName: 'website' | 'facebook' | 'instagram',
    value: string | null | undefined,
    setFormErrors: Dispatch<SetStateAction<FormErrors>>,
    t: (key: string, options?: any) => string
): void => {
    const str = value ?? "";
    if (!str.trim()) {
        setFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
        return;
    }

    if (!urlValidation.pattern.test(str)) {
        setFormErrors((prev) => ({ ...prev, [fieldName]: t('errors.invalidUrl') }));
    } else {
        setFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
};

export const validateDescription = (
    value: string | null | undefined,
    maxLength: number,
    setFormErrors: Dispatch<SetStateAction<FormErrors>>,
    t: (key: string, options?: any) => string
): void => {
    const str = value ?? "";
    if (str.length > maxLength) {
        setFormErrors((prev) => ({
            ...prev,
            businessDescription: t('errors.maxLength', { max: maxLength })
        }));
    } else {
        setFormErrors((prev) => ({ ...prev, businessDescription: "" }));
    }
};

export const validateWhatsappPhone = (
    phone: string | null | undefined,
    country: string | null | undefined,
    setFormErrors: Dispatch<SetStateAction<FormErrors>>,
    t: (key: string, options?: any) => string
): void => {
    const phoneStr = phone ?? "";
    const countryStr = country ?? "eg";
    if (!phoneStr.trim()) {
        setFormErrors((prev) => ({ ...prev, whatsappPhone: "" }));
        return;
    }

    const parsedPhoneNumber = phoneStr.replace(/[+\s]/g, '');

    if (parsedPhoneNumber.length === 0 || phoneStr === '+') {
        setFormErrors((prev) => ({ ...prev, whatsappPhone: t('errors.invalidWhatsapp') }));
        return;
    }

    let nationalNumber: string;

    if (countryStr === 'eg') {
        if (phoneStr.startsWith('+20')) {
            nationalNumber = phoneStr.substring(3);
        } else if (phoneStr.startsWith('+2')) {
            nationalNumber = phoneStr.substring(2);
        } else if (phoneStr.startsWith('20')) {
            nationalNumber = phoneStr.substring(2);
        } else if (phoneStr.startsWith('2') && phoneStr.length === 12) {
            nationalNumber = phoneStr.substring(1);
        } else {
            nationalNumber = phoneStr;
        }

        if (nationalNumber.startsWith('0')) {
            nationalNumber = nationalNumber.substring(1);
        }

        if (
            nationalNumber[0] !== '1' ||
            (nationalNumber[1] !== '0' && nationalNumber[1] !== '1' && nationalNumber[1] !== '2' && nationalNumber[1] !== '5') ||
            nationalNumber.length !== 10
        ) {
            setFormErrors((prev) => ({ ...prev, whatsappPhone: t('errors.invalidWhatsapp') }));
            return;
        }
    } else if (countryStr === 'ae') {
        if (phoneStr.startsWith('+971')) {
            nationalNumber = phoneStr.substring(4);
        } else if (phoneStr.startsWith('971')) {
            nationalNumber = phoneStr.substring(3);
        } else {
            nationalNumber = phoneStr;
        }

        if (nationalNumber[0] !== '5') {
            setFormErrors((prev) => ({ ...prev, whatsappPhone: t('errors.invalidWhatsapp') }));
            return;
        }
    } else {
        const digitsOnly = parsedPhoneNumber.replace(/\D/g, '');
        if (digitsOnly.length < 7) {
            setFormErrors((prev) => ({ ...prev, whatsappPhone: t('errors.invalidWhatsapp') }));
            return;
        }
    }

    setFormErrors((prev) => ({ ...prev, whatsappPhone: "" }));
};
